import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthLayout, FieldError } from '@/components/auth/AuthLayout';
import { api } from '@/lib/customFetch';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@/lib/auth-schemas';

export function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = handleSubmit(async (values) => {
    try { await api.post('/auth/forgot-password', values); } catch { /* intentional */ }
    setSent(true);
  });

  if (sent) {
    return (
      <AuthLayout eyebrow="Recuperação de acesso">
        <h2 style={{ fontSize: 26, fontWeight: 600, marginBottom: 8 }}>Verifique seu e-mail</h2>
        <p style={{ color: 'var(--muted-foreground)', fontSize: 14, marginBottom: 28 }}>
          Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha em alguns minutos.
        </p>
        <Button variant="outline" asChild style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Link to="/login"><ChevronLeft size={14} /> Voltar ao login</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="Recuperação de acesso">
      <h2 style={{ fontSize: 26, fontWeight: 600, marginBottom: 8 }}>Esqueceu a senha?</h2>
      <p style={{ color: 'var(--muted-foreground)', fontSize: 14, marginBottom: 28 }}>
        Informe o e-mail cadastrado e enviaremos um link de redefinição.
      </p>

      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 22 }}>
          <Label htmlFor="email" style={{ display: 'block', marginBottom: 6 }}>E-mail institucional</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="seu.email@evertec.com.br"
            {...register('email')}
          />
          {errors.email && <FieldError>{errors.email.message}</FieldError>}
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting}
          style={{ marginBottom: 12 }}
        >
          Enviar link de redefinição
        </Button>

        <Button variant="ghost" className="w-full" asChild>
          <Link to="/login" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' }}>
            <ChevronLeft size={14} /> Voltar ao login
          </Link>
        </Button>
      </form>

      <div style={{ borderTop: '1px solid var(--border)', marginTop: 28, paddingTop: 18, fontSize: 12, color: 'var(--muted-foreground)', textAlign: 'center' }}>
        Se você não receber o e-mail em até 5 minutos, contate{' '}
        <a href="mailto:banesprevsecretariageral@santander.com.br" style={{ color: 'var(--ev-accent)' }}>
          banesprevsecretariageral@santander.com.br
        </a>.
      </div>
    </AuthLayout>
  );
}
